import React, { useState } from 'react';
import { api, ApiError } from '@/api/client';
import { useMe } from '@/context/MeContext';
import type { BillingStatus } from '@/types';

const PLAN_BLURBS: Record<string, string> = {
  starter: '20 searches · 20 subscriptions · 5 sources',
  pro: '100 searches · 100 subscriptions · 20 sources',
  business: '1000 searches · 1000 subscriptions · 100 sources',
};

type Props = {
  billing: BillingStatus | null;
  onRefresh: () => void;
};

export default function UpgradePlanSection({ billing, onRefresh }: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const { me } = useMe();
  const canCheckout = me?.permissions.canUseBillingCheckout ?? false;

  if (!billing) return null;

  async function startCheckout(plan: string) {
    setBusy(plan);
    setError('');
    try {
      const { checkoutUrl } = await api.billing.checkout(plan, billing?.userId ?? undefined);
      window.location.href = checkoutUrl;
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? typeof e.detail === 'object' && e.detail && 'message' in (e.detail as object)
            ? String((e.detail as { message: string }).message)
            : e.message
          : e instanceof Error
            ? e.message
            : 'Checkout failed';
      setError(msg);
      setBusy(null);
    }
  }

  async function openPortal() {
    setBusy('portal');
    setError('');
    try {
      const { portalUrl } = await api.billing.portal(billing?.userId ?? undefined);
      window.location.href = portalUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Portal failed');
      setBusy(null);
    }
  }

  const planLabel = billing.plan.charAt(0).toUpperCase() + billing.plan.slice(1);

  return (
    <div className="upgrade-plan-section">
      {!billing.onlinePaymentsAvailable && (
        <p className="muted billing-disabled-note">
          Online payments are not currently available. Contact Scoutly support for plan changes.
        </p>
      )}
      {billing.onlinePaymentsAvailable && billing.subscriptionStatus && (
        <p className="muted">
          Subscription: <strong>{billing.subscriptionStatus}</strong>
          {billing.currentPeriodEnd && (
            <> · renews {new Date(billing.currentPeriodEnd).toLocaleDateString()}</>
          )}
        </p>
      )}
      {billing.onlinePaymentsAvailable && canCheckout && (
        <div className="upgrade-actions">
          {billing.plan !== 'free' && billing.subscriptionStatus && (
            <button
              type="button"
              className="btn"
              disabled={busy !== null}
              onClick={() => openPortal()}
            >
              {busy === 'portal' ? 'Opening…' : 'Manage billing'}
            </button>
          )}
        </div>
      )}
      {billing.onlinePaymentsAvailable && canCheckout && billing.plan === 'free' && (
        <>
          <p className="muted">Upgrade from {planLabel}</p>
          <div className="plan-cards">
            {billing.availablePlans.map((plan) => (
              <div key={plan} className="card plan-card">
                <h3>{plan.charAt(0).toUpperCase() + plan.slice(1)}</h3>
                <p className="muted plan-blurb">{PLAN_BLURBS[plan] ?? plan}</p>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy !== null}
                  onClick={() => startCheckout(plan)}
                >
                  {busy === plan ? 'Redirecting…' : 'Upgrade'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
      {error && <div className="error">{error}</div>}
      {billing.onlinePaymentsAvailable && (
        <button type="button" className="btn btn-link" onClick={onRefresh}>
          Refresh billing status
        </button>
      )}
    </div>
  );
}
